import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ActivityTrackingProvider } from "./contexts/ActivityTrackingContext";

import { useFirstTimeUser } from './hooks/useFirstTimeUser';
import { CustomCategoriesLoader } from './components/CustomCategoriesLoader';
import { DateFormatLoader } from './components/DateFormatLoader';
import { LanguageSync } from './hooks/useLanguage';
import { LocalizedPrefixRedirect } from './components/system/LocalizedPrefixRedirect';
import { I18nDirectionProvider } from './components/system/I18nDirectionProvider';
import { FamilyScopeProvider } from './contexts/FamilyScopeContext';
import { ChunkErrorBoundary } from './components/system/ChunkErrorBoundary';
import { AppShell } from './components/layout/AppShell';
import { getFennieArtwork } from './lib/fennieStageAssets';

const BABY_FENNIE_LOADER = getFennieArtwork('baby', 'healthy');

const FennieLoader = ({ label }: { label?: string }) => {
  const { t } = useTranslation('common');
  return (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <img
        src={BABY_FENNIE_LOADER}
        alt={t('loading.fennieAlt')}
        className="h-32 w-32 object-contain animate-pulse drop-shadow-[0_8px_24px_rgba(212,175,55,0.25)]"
        draggable={false}
      />
      <p className="text-sm text-muted-foreground">{label ?? t('loading.label')}</p>
    </div>
  </div>
  );
};

// Critical route - only landing page loaded immediately
import Landing from "./pages/Landing";
import { lazyWithRetry } from "./lib/lazyWithRetry";

const LandingCouples = lazyWithRetry(() => import("./pages/LandingCouples"));
const LandingAntiBudget = lazyWithRetry(() => import("./pages/LandingAntiBudget"));
const LandingStudents = lazyWithRetry(() => import("./pages/LandingStudents"));
const LandingArabic = lazyWithRetry(() => import("./pages/LandingArabic"));
const LandingSummerTravel = lazyWithRetry(() => import("./pages/LandingSummerTravel"));
const LandingSummerTravelArabic = lazyWithRetry(() => import("./pages/LandingSummerTravelArabic"));

// Lazy-loaded critical routes - loaded on first navigation
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

// Lazy-loaded routes - loaded on demand
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const RefundPolicy = lazyWithRetry(() => import("./pages/RefundPolicy"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const Goals = lazyWithRetry(() => import("./pages/Goals"));
const JoinGoal = lazyWithRetry(() => import("./pages/JoinGoal"));
const AddExpense = lazyWithRetry(() => import("./pages/AddExpense"));
const Insights = lazyWithRetry(() => import("./pages/Insights"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const SettingsAccount = lazyWithRetry(() => import("./pages/settings/Account"));
const SettingsMoney = lazyWithRetry(() => import("./pages/settings/Money"));
const SettingsDashboardLayout = lazyWithRetry(() => import("./pages/settings/DashboardLayout"));
const SettingsFennie = lazyWithRetry(() => import("./pages/settings/Fennie"));
const SettingsEmails = lazyWithRetry(() => import("./pages/settings/Emails"));

const SettingsSupport = lazyWithRetry(() => import("./pages/settings/Support"));
const SettingsRecurringActivity = lazyWithRetry(() => import("./pages/settings/RecurringActivity"));
const SettingsCalendarSync = lazyWithRetry(() => import("./pages/settings/CalendarSync"));
const SettingsCalendarSyncCallback = lazyWithRetry(() => import("./pages/settings/CalendarSyncCallback"));
const Calendar = lazyWithRetry(() => import("./pages/Calendar"));
const UpcomingExpenses = lazyWithRetry(() => import("./pages/UpcomingExpenses"));
const BudgetDetails = lazyWithRetry(() => import("./pages/BudgetDetails"));
const IncomeManagement = lazyWithRetry(() => import("./pages/IncomeManagement"));
const Documents = lazyWithRetry(() => import("./pages/Documents"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const Roadmap = lazyWithRetry(() => import("./pages/Roadmap"));
const ManageSubscription = lazyWithRetry(() => import("./pages/ManageSubscription"));
const FennieJourney = lazyWithRetry(() => import("./pages/FennieJourney"));
const Family = lazyWithRetry(() => import("./pages/Family"));
const JoinFamily = lazyWithRetry(() => import("./pages/JoinFamily"));
const ScannerHome = lazyWithRetry(() => import("./pages/ScannerHome"));
const PendingApprovals = lazyWithRetry(() => import("./pages/PendingApprovals"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const Creators = lazyWithRetry(() => import("./pages/Creators"));
const OAuthConsent = lazyWithRetry(() => import("./pages/OAuthConsent"));

// Admin route component - loaded separately for code splitting
import { AdminRoute } from './components/auth/AdminRoute';
import { PremiumRoute } from './components/auth/PremiumRoute';

// Simple redirect component for /join?ref=CODE
function JoinReferral() {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref') || '';
  return <Navigate to={`/auth?ref=${ref}`} replace />;
}

const queryClient = new QueryClient();

// Lightweight page loader for lazy-loaded routes
const PageLoader = () => <FennieLoader />;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <FennieLoader />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

function OnboardingRedirect({ children }: { children: React.ReactNode }) {
  const { isFirstTime, loading } = useFirstTimeUser();

  if (loading) {
    return <FennieLoader />;
  }

  if (isFirstTime) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <I18nDirectionProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <BrowserRouter>
          <ActivityTrackingProvider>
            <FamilyScopeProvider>
            <CustomCategoriesLoader />
            <DateFormatLoader />
            <LanguageSync />
            <ChunkErrorBoundary>
            <Routes>
            {/* Public landing page */}
            <Route path="/" element={<Landing />} />
            <Route path="/v/couples" element={
              <Suspense fallback={<PageLoader />}>
                <LandingCouples />
              </Suspense>
            } />
            <Route path="/v/anti-budget" element={
              <Suspense fallback={<PageLoader />}>
                <LandingAntiBudget />
              </Suspense>
            } />
            <Route path="/v/students" element={
              <Suspense fallback={<PageLoader />}>
                <LandingStudents />
              </Suspense>
            } />
            <Route path="/v/summer-travel" element={
              <Suspense fallback={<PageLoader />}>
                <LandingSummerTravel />
              </Suspense>
            } />
            <Route path="/v/summer-travel-ar" element={
              <Suspense fallback={<PageLoader />}>
                <LandingSummerTravelArabic />
              </Suspense>
            } />
            <Route path="/ar/*" element={<LocalizedPrefixRedirect />} />
            <Route path="/es/*" element={<LocalizedPrefixRedirect />} />
            <Route path="/en/*" element={<LocalizedPrefixRedirect />} />
            <Route path="/ar" element={<LocalizedPrefixRedirect />} />
            <Route path="/es" element={<LocalizedPrefixRedirect />} />
            <Route path="/en" element={<LocalizedPrefixRedirect />} />
            <Route path="/ar-preview" element={
              <Suspense fallback={<PageLoader />}>
                <LandingArabic />
              </Suspense>
            } />
            <Route path="/auth" element={
              <Suspense fallback={<PageLoader />}>
                <Auth />
              </Suspense>
            } />
            <Route path="/privacy" element={
              <Suspense fallback={<PageLoader />}>
                <PrivacyPolicy />
              </Suspense>
            } />
            <Route path="/terms" element={
              <Suspense fallback={<PageLoader />}>
                <TermsOfService />
              </Suspense>
            } />
            <Route path="/refund" element={
              <Suspense fallback={<PageLoader />}>
                <RefundPolicy />
              </Suspense>
            } />
            <Route path="/unsubscribe/:token?" element={
              <Suspense fallback={<PageLoader />}>
                <Unsubscribe />
              </Suspense>
            } />
            <Route path="/pricing" element={
              <Suspense fallback={<PageLoader />}>
                <Pricing />
              </Suspense>
            } />
            <Route path="/roadmap" element={
              <Suspense fallback={<PageLoader />}>
                <Roadmap />
              </Suspense>
            } />
            <Route path="/creators" element={
              <Suspense fallback={<PageLoader />}>
                <Creators />
              </Suspense>
            } />
            {/* Legacy/alternate affiliate entry points funnel into /creators */}
            <Route path="/.lovable/oauth/consent" element={
              <Suspense fallback={<PageLoader />}>
                <OAuthConsent />
              </Suspense>
            } />
            <Route path="/affiliate" element={<Navigate to="/creators" replace />} />
            <Route path="/affiliates" element={<Navigate to="/creators" replace />} />
            <Route path="/influencer" element={<Navigate to="/creators" replace />} />
            <Route path="/influencers" element={<Navigate to="/creators" replace />} />
            
            
            {/* Protected routes */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <Onboarding />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                    <Suspense fallback={<PageLoader />}>
                      <Dashboard />
                    </Suspense>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/scanner" element={
              <ProtectedRoute>
                <AppShell hideRecap>
                  <Suspense fallback={<PageLoader />}>
                    <ScannerHome />
                  </Suspense>
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/goals" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <Suspense fallback={<PageLoader />}>
                      <Goals />
                    </Suspense>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/goals/join/:token" element={
              <Suspense fallback={<PageLoader />}>
                <JoinGoal />
              </Suspense>
            } />
            <Route path="/add" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                    <Suspense fallback={<PageLoader />}>
                      <AddExpense />
                    </Suspense>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/insights" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <PremiumRoute context="insights">
                      <Suspense fallback={<PageLoader />}>
                        <Insights />
                      </Suspense>
                    </PremiumRoute>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <Suspense fallback={<PageLoader />}>
                      <Settings />
                    </Suspense>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/settings/account" element={
              <ProtectedRoute><OnboardingRedirect><AppShell>
                <Suspense fallback={<PageLoader />}><SettingsAccount /></Suspense>
              </AppShell></OnboardingRedirect></ProtectedRoute>
            } />
            <Route path="/settings/money" element={
              <ProtectedRoute><OnboardingRedirect><AppShell>
                <Suspense fallback={<PageLoader />}><SettingsMoney /></Suspense>
              </AppShell></OnboardingRedirect></ProtectedRoute>
            } />
            <Route path="/settings/dashboard" element={
              <ProtectedRoute><OnboardingRedirect><AppShell>
                <Suspense fallback={<PageLoader />}><SettingsDashboardLayout /></Suspense>
              </AppShell></OnboardingRedirect></ProtectedRoute>
            } />
            <Route path="/settings/fennie" element={
              <ProtectedRoute><OnboardingRedirect><AppShell>
                <Suspense fallback={<PageLoader />}><SettingsFennie /></Suspense>
              </AppShell></OnboardingRedirect></ProtectedRoute>
            } />
            <Route path="/settings/emails" element={
              <ProtectedRoute><OnboardingRedirect><AppShell>
                <Suspense fallback={<PageLoader />}><SettingsEmails /></Suspense>
              </AppShell></OnboardingRedirect></ProtectedRoute>
            } />

            <Route path="/settings/support" element={
              <ProtectedRoute><OnboardingRedirect><AppShell>
                <Suspense fallback={<PageLoader />}><SettingsSupport /></Suspense>
              </AppShell></OnboardingRedirect></ProtectedRoute>
            } />
            <Route path="/settings/recurring" element={
              <ProtectedRoute><OnboardingRedirect><AppShell>
                <Suspense fallback={<PageLoader />}><SettingsRecurringActivity /></Suspense>
              </AppShell></OnboardingRedirect></ProtectedRoute>
            } />
            <Route path="/settings/calendar-sync" element={
              <ProtectedRoute><AdminRoute><OnboardingRedirect><AppShell>
                <Suspense fallback={<PageLoader />}><SettingsCalendarSync /></Suspense>
              </AppShell></OnboardingRedirect></AdminRoute></ProtectedRoute>
            } />
            <Route path="/settings/calendar-sync/callback" element={
              <ProtectedRoute><AdminRoute>
                <Suspense fallback={<PageLoader />}><SettingsCalendarSyncCallback /></Suspense>
              </AdminRoute></ProtectedRoute>
            } />
            <Route path="/calendar" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <Suspense fallback={<PageLoader />}>
                      <Calendar />
                    </Suspense>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />

            <Route path="/upcoming" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <Suspense fallback={<PageLoader />}>
                      <UpcomingExpenses />
                    </Suspense>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/budget-details" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <Suspense fallback={<PageLoader />}>
                      <BudgetDetails />
                    </Suspense>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/income" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <Suspense fallback={<PageLoader />}>
                      <IncomeManagement />
                    </Suspense>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/documents" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <Suspense fallback={<PageLoader />}>
                      <Documents />
                    </Suspense>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/subscription" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <Suspense fallback={<PageLoader />}>
                      <ManageSubscription />
                    </Suspense>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/journey" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <PremiumRoute context="journey">
                      <Suspense fallback={<PageLoader />}>
                        <FennieJourney />
                      </Suspense>
                    </PremiumRoute>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/family" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <PremiumRoute context="family">
                      <Suspense fallback={<PageLoader />}>
                        <Family />
                      </Suspense>
                    </PremiumRoute>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/family/approvals" element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <PremiumRoute context="family">
                      <Suspense fallback={<PageLoader />}>
                        <PendingApprovals />
                      </Suspense>
                    </PremiumRoute>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            } />
            <Route path="/join-family" element={
              <Suspense fallback={<PageLoader />}>
                <JoinFamily />
              </Suspense>
            } />
            {/* Admin route */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AdminDashboard />
                  </Suspense>
                </AdminRoute>
              </ProtectedRoute>
            } />
            {/* /join redirects to /auth with ref param */}
            <Route path="/join" element={<JoinReferral />} />
            {/* /recurring now redirects to /upcoming */}
            <Route path="/recurring" element={<Navigate to="/upcoming" replace />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={
              <Suspense fallback={<PageLoader />}>
                <NotFound />
              </Suspense>
            } />
          </Routes>
          </ChunkErrorBoundary>
          </FamilyScopeProvider>
          </ActivityTrackingProvider>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
    </I18nDirectionProvider>
  </QueryClientProvider>
);

export default App;
